// src/components/CariHesaplar.js

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, TextField, InputAdornment, Button, Tabs, Tab, Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PageviewIcon from '@mui/icons-material/Pageview';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PlanlanmisOdemeModal from './PlanlanmisOdemeModal';
import { normalizeTr, aramaMetniUret } from '../utils/search';

const formatCurrency = (amount) => (amount ?? 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
const formatDate = (ts) => (ts && typeof ts.toDate === 'function') ? ts.toDate().toLocaleDateString('tr-TR') : '-';

// G10: Borçlular (varsayılan) + Tümü sekmeleri. Borçlular bakiye büyükten küçüğe;
// satırda borç başlangıç tarihi (en eski kalanTutar>0 iş emri) + en yakın ödeme sözü.
const CariHesaplar = () => {
    const [customers, setCustomers] = useState([]);
    const [borcluIsEmirleri, setBorcluIsEmirleri] = useState([]);
    const [odemeSozleri, setOdemeSozleri] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [aktifSekme, setAktifSekme] = useState('borclular');
    const [planlamaMusterisi, setPlanlamaMusterisi] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const unsubCustomers = onSnapshot(query(collection(db, "customers")), (snapshot) => {
            setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        // Tek toplu sorgu: borç başlangıç tarihi istemcide türetilir (kalanTutar>0 iş emirleri).
        const unsubWo = onSnapshot(query(collection(db, "workOrders"), where("kalanTutar", ">", 0)), (snapshot) => {
            setBorcluIsEmirleri(snapshot.docs.map(doc => {
                const d = doc.data();
                return { musteriId: d.musteriId, isEmriTarihi: d.isEmriTarihi };
            }));
        });
        // Ödeme sözleri: yalnız 'Bekleniyor' durumundakiler (G5: scheduledPayments = ödeme sözü mekanizması).
        const unsubSp = onSnapshot(query(collection(db, "scheduledPayments"), where("durum", "==", "Bekleniyor")), (snapshot) => {
            setOdemeSozleri(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => { unsubCustomers(); unsubWo(); unsubSp(); };
    }, []);

    // musteriId → en eski kalanTutar>0 iş emri tarihi
    const borcBaslangicMap = useMemo(() => {
        const map = {};
        borcluIsEmirleri.forEach(wo => {
            if (!wo.musteriId || !wo.isEmriTarihi) return;
            const millis = wo.isEmriTarihi.toMillis ? wo.isEmriTarihi.toMillis() : 0;
            if (!map[wo.musteriId] || millis < map[wo.musteriId].millis) {
                map[wo.musteriId] = { millis, tarih: wo.isEmriTarihi };
            }
        });
        return map;
    }, [borcluIsEmirleri]);

    // musteriId → en yakın 'Bekleniyor' ödeme sözü
    const odemeSozuMap = useMemo(() => {
        const map = {};
        odemeSozleri.forEach(sp => {
            if (!sp.musteriId || !sp.vadeTarihi) return;
            const millis = sp.vadeTarihi.toMillis ? sp.vadeTarihi.toMillis() : 0;
            if (!map[sp.musteriId] || millis < map[sp.musteriId].millis) {
                map[sp.musteriId] = { millis, vadeTarihi: sp.vadeTarihi, tutar: sp.tutar };
            }
        });
        return map;
    }, [odemeSozleri]);

    // G8: Türkçe-normalize arama (ad + telefon), null-guard'lı.
    const filteredData = useMemo(() => {
        const q = normalizeTr(searchTerm).trim();
        if (!q) return customers;
        return customers.filter(customer =>
            aramaMetniUret(customer.adSoyad, customer.telefon).includes(q)
        );
    }, [customers, searchTerm]);

    const borclular = useMemo(() => (
        filteredData
            .filter(c => (c.toplamBakiye || 0) > 0)
            .sort((a, b) => (b.toplamBakiye || 0) - (a.toplamBakiye || 0))
    ), [filteredData]);

    const tumu = useMemo(() => (
        [...filteredData].sort((a, b) => (b.toplamBakiye || 0) - (a.toplamBakiye || 0))
    ), [filteredData]);

    if (loading) { return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />; }

    const simdi = Date.now();

    const renderOdemeSozu = (customerId) => {
        const soz = odemeSozuMap[customerId];
        if (!soz) return <Typography variant="caption" color="text.secondary">-</Typography>;
        const gecmis = soz.millis < simdi;
        return (
            <Chip
                size="small"
                color={gecmis ? 'error' : 'info'}
                variant={gecmis ? 'filled' : 'outlined'}
                label={`${formatDate(soz.vadeTarihi)} · ${formatCurrency(soz.tutar)}${gecmis ? ' — GECİKTİ' : ''}`}
            />
        );
    };

    return (
        <Box>
            <PlanlanmisOdemeModal
                isOpen={Boolean(planlamaMusterisi)}
                onClose={() => setPlanlamaMusterisi(null)}
                customer={planlamaMusterisi || { id: '', adSoyad: '' }}
                finalBalance={planlamaMusterisi?.toplamBakiye || 0}
                paymentToEdit={null}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" component="h1">Cari Hesaplar</Typography>
                <TextField size="small" variant="outlined" placeholder="Müşteri Ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} />
            </Box>

            <Tabs value={aktifSekme} onChange={(e, v) => setAktifSekme(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tab value="borclular" label={`Borçlular (${borclular.length})`} />
                <Tab value="tumu" label={`Tümü (${tumu.length})`} />
            </Tabs>

            {aktifSekme === 'borclular' ? (
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Müşteri Adı</TableCell>
                                    <TableCell align="right">Bakiye</TableCell>
                                    <TableCell>Borç Başlangıcı</TableCell>
                                    <TableCell>Ödeme Sözü</TableCell>
                                    <TableCell align="center">İşlemler</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {borclular.length === 0 && (
                                    <TableRow><TableCell colSpan={5} align="center">Borçlu müşteri bulunmuyor.</TableCell></TableRow>
                                )}
                                {borclular.map(customer => (
                                    <TableRow hover key={customer.id} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/cari-hesaplar/${customer.id}`)}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{customer.adSoyad}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                            {formatCurrency(customer.toplamBakiye)}
                                        </TableCell>
                                        <TableCell>
                                            {borcBaslangicMap[customer.id]
                                                ? formatDate(borcBaslangicMap[customer.id].tarih)
                                                : <Typography variant="caption" color="text.secondary">İş emri dışı borç</Typography>}
                                        </TableCell>
                                        <TableCell>{renderOdemeSozu(customer.id)}</TableCell>
                                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                            <Button size="small" variant="outlined" startIcon={<PlaylistAddCheckIcon />} sx={{ mr: 1 }} onClick={() => setPlanlamaMusterisi(customer)}>
                                                Ödeme Sözü
                                            </Button>
                                            <Button size="small" variant="outlined" component={RouterLink} to={`/cari-hesaplar/${customer.id}`} startIcon={<PageviewIcon />}>
                                                Ekstre
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Müşteri Adı</TableCell>
                                    <TableCell align="right">Toplam Ciro</TableCell>
                                    <TableCell align="right">Toplam Tahsilat</TableCell>
                                    <TableCell align="right">Güncel Bakiye</TableCell>
                                    <TableCell align="center">İşlemler</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tumu.map(customer => (
                                    <TableRow hover key={customer.id} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/cari-hesaplar/${customer.id}`)}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{customer.adSoyad}</TableCell>
                                        <TableCell align="right">{formatCurrency(customer.toplamSatis)}</TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main' }}>{formatCurrency(customer.toplamOdeme)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: customer.toplamBakiye > 0 ? 'error.main' : 'inherit' }}>
                                            {formatCurrency(customer.toplamBakiye)}
                                        </TableCell>
                                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                            <Button variant="outlined" size="small" component={RouterLink} to={`/cari-hesaplar/${customer.id}`} startIcon={<PageviewIcon />}>
                                                Ekstre
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
};

export default CariHesaplar;
